import { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { Clock, Search, RefreshCw, Save, Undo2, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const today = () => new Date().toISOString().slice(0, 10);

// 'YYYY-MM-DD HH:MM:SS.mmm' -> 'YYYY-MM-DDTHH:MM' for a datetime-local input.
const toInput = (s) => {
  if (!s) return '';
  const m = String(s).match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
  return m ? `${m[1]}T${m[2]}` : '';
};
// 'YYYY-MM-DDTHH:MM' -> 'YYYY-MM-DD HH:MM:00'
const fromInput = (v) => (v ? `${v.replace('T', ' ')}:00` : null);
const shortTs = (s) => (s ? String(s).replace(/\.\d+$/, '').replace(/^\d{4}-/, '') : '—');

export default function SessionCorrections() {
  const { isAdmin } = useAuth();
  const [date, setDate] = useState(today());
  const [q, setQ] = useState('');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [corrections, setCorrections] = useState([]);
  const [editKey, setEditKey] = useState(null);
  const [form, setForm] = useState({ newStart: '', newEnd: '', note: '' });
  const [saving, setSaving] = useState(false);

  const loadCorrections = useCallback(async () => {
    try { const r = await api.get('/admin/session-corrections'); setCorrections(r.data.corrections || []); }
    catch { /* */ }
  }, []);
  useEffect(() => { loadCorrections(); }, [loadCorrections]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/session-corrections/sessions', { params: { date, q: q || undefined } });
      setSessions(r.data.sessions || []);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not load sessions');
    } finally { setLoading(false); }
  };

  const beginEdit = (s) => {
    setEditKey(s.key);
    setForm({ newStart: toInput(s.openDate), newEnd: toInput(s.closedDate), note: '' });
  };

  const save = async (s) => {
    const newStart = fromInput(form.newStart);
    const newEnd = fromInput(form.newEnd);
    if (!newStart && !newEnd) { toast.error('Enter a corrected start and/or end'); return; }
    if (newStart && newEnd && newEnd < newStart) { toast.error('End cannot be before start'); return; }
    setSaving(true);
    try {
      await api.post('/admin/session-corrections', {
        tableName: s.tableName,
        tablecaptainPlayerId: s.playerId,
        openDateStr: s.openDate,
        businessDate: date,
        playerName: s.playerName,
        gameName: s.gameName,
        origOpen: s.openDate,
        origClose: s.closedDate,
        newStart,
        newEnd,
        note: form.note || null,
      });
      toast.success('Correction saved');
      setEditKey(null);
      loadCorrections();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save');
    } finally { setSaving(false); }
  };

  const voidOne = async (id) => {
    if (!window.confirm('Undo this correction? The session reverts to its original TableCaptain times.')) return;
    try { await api.post(`/admin/session-corrections/${id}/void`); toast.success('Correction undone'); loadCorrections(); }
    catch (e) { toast.error(e.response?.data?.error || 'Failed to undo'); }
  };

  const activeKeys = new Set(corrections.filter((c) => c.active).map((c) => `${c.tableName}|${c.tablecaptainPlayerId}|${c.openDateStr}`));

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-2 mb-1"><Clock className="text-amber-400" size={22} /><h1 className="text-2xl font-bold text-white">Play-Time Corrections</h1></div>
      <p className="text-gray-400 text-sm mb-6">Amend a session's start/end time. Non-destructive and audited; applied on top of TableCaptain data — it never edits the source record.</p>

      {/* Find sessions */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div><label className="block text-xs text-gray-400 mb-1">Gaming day</label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" /></div>
          <div className="flex-1 min-w-[200px]"><label className="block text-xs text-gray-400 mb-1">Player (optional)</label><input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name…" className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white" /></div>
          <button onClick={loadSessions} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-black font-semibold rounded-lg">{loading ? <RefreshCw size={16} className="animate-spin" /> : <Search size={16} />} Find sessions</button>
        </div>
      </div>

      {/* Session list */}
      {sessions.length > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl divide-y divide-gray-700 mb-6">
          {sessions.map((s) => (
            <div key={s.key} className="p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="text-sm">
                  <span className="text-white font-medium">{s.playerName || 'Guest'}</span>
                  <span className="text-gray-400"> · Table {s.tableName} · {s.gameName || '—'}</span>
                  <div className="text-gray-500 text-xs mt-0.5">{shortTs(s.openDate)} → {shortTs(s.closedDate)} · {s.spanMinutes} min{activeKeys.has(s.key) ? ' · corrected' : ''}{s.lingeringPastClose ? ' · lingering' : ''}</div>
                </div>
                {isAdmin && editKey !== s.key && <button onClick={() => beginEdit(s)} className="flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300"><Pencil size={14} /> Correct</button>}
              </div>
              {editKey === s.key && (
                <div className="mt-3 flex flex-wrap items-end gap-3 bg-gray-900/60 rounded-lg p-3">
                  <div><label className="block text-xs text-gray-400 mb-1">Corrected start</label><input type="datetime-local" value={form.newStart} onChange={(e) => setForm({ ...form, newStart: e.target.value })} className="bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-white text-sm" /></div>
                  <div><label className="block text-xs text-gray-400 mb-1">Corrected end</label><input type="datetime-local" value={form.newEnd} onChange={(e) => setForm({ ...form, newEnd: e.target.value })} className="bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-white text-sm" /></div>
                  <div className="flex-1 min-w-[180px]"><label className="block text-xs text-gray-400 mb-1">Reason</label><input type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. camera-verified seat-out 3:37pm" className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-white text-sm" /></div>
                  <button onClick={() => save(s)} disabled={saving} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white text-sm font-medium rounded"><Save size={14} /> Save</button>
                  <button onClick={() => setEditKey(null)} className="px-3 py-1.5 text-gray-400 hover:text-white text-sm">Cancel</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Existing corrections */}
      <h2 className="text-lg font-semibold text-white mb-2">Corrections</h2>
      <div className="bg-gray-800 border border-gray-700 rounded-xl">
        {corrections.length === 0 ? <p className="text-gray-500 p-4 text-sm">No corrections yet.</p> : (
          <div className="divide-y divide-gray-700">
            {corrections.map((c) => (
              <div key={c.id} className={`p-3 flex items-center justify-between gap-3 text-sm ${c.active ? '' : 'opacity-50'}`}>
                <div>
                  <div className="text-gray-200"><span className="font-medium">{c.playerName || 'Guest'}</span><span className="text-gray-400"> · Table {c.tableName} · {c.gameName || '—'}</span>{!c.active && <span className="text-gray-500"> · undone</span>}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{shortTs(c.origOpen)}→{shortTs(c.origClose)} ⇒ {shortTs(c.newStart || c.origOpen)}→{shortTs(c.newEnd || c.origClose)}{c.note ? ` · ${c.note}` : ''}{c.createdByName ? ` · by ${c.createdByName}` : ''}</div>
                </div>
                {isAdmin && c.active && <button onClick={() => voidOne(c.id)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-400"><Undo2 size={14} /> Undo</button>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
